/**
 * Custom color configuration for PrimeVue Tags (roles + departments).
 * Returns inline style objects: background, text color, border color.
 * Keep mappings strict to avoid surprising partial matches.
 */

/** Convert hex to RGB */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/** Calculate relative luminance of a color (0-1) */
const getLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map(val => {
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Auto-determine text color (white/black) based on background brightness */
const getAutoTextColor = (bgHex) => {
  const luminance = getLuminance(bgHex);
  // Use white text on dark backgrounds (luminance < 0.5), black on light
  return luminance < 0.5 ? '#ffffff' : '#111827';
};

/** Normalize string keys (role code / department name) */
const norm = (v) => (v ?? '').toString().toLowerCase().trim();

const tagPalette = [
  { bg: '#16a34a', text: getAutoTextColor('#16a34a'), border: '#15803d' }, // green
  { bg: '#2563eb', text: getAutoTextColor('#2563eb'), border: '#1d4ed8' }, // blue
  { bg: '#f59e0b', text: getAutoTextColor('#f59e0b'), border: '#d97706' }, // amber
  { bg: '#ef4444', text: getAutoTextColor('#ef4444'), border: '#dc2626' }, // red
  { bg: '#8b5cf6', text: getAutoTextColor('#8b5cf6'), border: '#7c3aed' }, // violet
  { bg: '#64748b', text: getAutoTextColor('#64748b'), border: '#475569' }, // slate
];

/** Role colors by role code */
export const roleTagColors = {
  super_admin: { bg: '#ef4444', text: '#ffffff', border: '#dc2626' }, // red
  dept_admin:  { bg: '#f59e0b', text: '#111827', border: '#d97706' }, // amber
  dept_user:   { bg: '#2563eb', text: '#ffffff', border: '#1d4ed8' }, // blue
};

/** Optional: department colors by ID (if you want absolute control for specific IDs) */
export const departmentTagColorsById = {
  // Example:
  // 1: { bg: '#ef4444', text: getAutoTextColor('#ef4444'), border: '#dc2626' },
};

/** Role display names mapping */
export const roleDisplayNames = {
  super_admin: 'Super Admin',
  dept_admin: 'Admin',
  dept_user: 'User',
};

/**
 * Get display name for role
 * @param {string} roleCode
 * @returns {string} Display name
 */
export const getRoleDisplayName = (roleCode) => {
  const key = norm(roleCode);
  if (roleDisplayNames[key]) {
    return roleDisplayNames[key];
  }
  // Fallback: capitalize and replace underscores
  return key.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/** Deterministic hash for stable fallback colors */
const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0; // 32-bit
  }
  return Math.abs(h);
};

/** Build style object for PrimeVue Tag */
const toTagStyle = ({ bg, text, border }) => {
  // Auto-determine text color if not explicitly provided
  const finalText = text || getAutoTextColor(bg);
  return {
    backgroundColor: bg,
    color: finalText,
    border: `1px solid ${border}`,
  };
};

/**
 * Get role severity for PrimeVue Tag
 * @param {string} roleCode
 * @returns {string} PrimeVue severity value
 */
export const getRoleSeverity = (roleCode) => {
  const key = norm(roleCode);
  
  // Map role codes to PrimeVue severity
  if (key === 'super_admin') return 'danger';
  if (key === 'dept_admin') return 'warning';
  if (key === 'dept_user') return 'info';
  
  // Fallback: try to match by partial key
  if (key.includes('admin') || key.includes('super')) return 'danger';
  if (key.includes('fleet')) return 'warning';
  if (key.includes('service') || key.includes('road')) return 'info';
  if (key.includes('shop')) return 'success';
  
  return 'secondary';
};

const normalizeHex6 = (c) => {
  if (!c) return null;
  const s = String(c).trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s;
  if (/^[0-9A-Fa-f]{6}$/i.test(s)) return `#${s}`;
  return null;
};

/**
 * Get department tag style by (id, name, tag_color, tag_text_color)
 * Priority: DB colors > id mapping > id palette > name hash
 * Colors are now stored in DB (tag_color, tag_text_color fields)
 * @param {number|string|null} departmentId
 * @param {string|null} departmentName
 * @param {string|null} tagColor - HEX color from DB (e.g. #22C55E)
 * @param {string|null} tagTextColor - HEX text color from DB (e.g. #FFFFFF)
 * @returns {object} style for <Tag :style="...">
 */
export const getDepartmentTagStyle = (departmentId, departmentName = null, tagColor = null, tagTextColor = null) => {
  const bgNorm = normalizeHex6(tagColor);
  const textNorm = normalizeHex6(tagTextColor);
  // Priority 1: Use colors from DB if provided
  if (bgNorm) {
    const bg = bgNorm;
    const text = textNorm ? textNorm : getAutoTextColor(bg);
    const border = bg; // Use same color for border, or could darken it
    return toTagStyle({ bg, text, border });
  }

  // Priority 2: ID-based mapping (optional override for specific IDs)
  const idNum = departmentId !== null && departmentId !== undefined && departmentId !== ''
    ? Number(departmentId)
    : null;

  if (idNum && departmentTagColorsById[idNum]) {
    return toTagStyle(departmentTagColorsById[idNum]);
  }

  // Priority 3: ID-based palette (fallback)
  if (idNum) {
    const idx = (idNum - 1) % tagPalette.length;
    return toTagStyle(tagPalette[idx]);
  }

  // Priority 4: Name-based hash (fallback)
  const nameKey = norm(departmentName);
  if (nameKey) {
    const idx = hashString(nameKey) % tagPalette.length;
    return toTagStyle(tagPalette[idx]);
  }

  // Ultimate fallback
  return toTagStyle(tagPalette[tagPalette.length - 1]);
};

/** Alias for teams, sub-departments, and other reference entities with tag_color. */
export const getReferenceTagStyle = getDepartmentTagStyle;
